import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo } from 'react';
import { CriticalIcon } from '@paypalcorp/pp-react-icons';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles, CaptionText } from '@paypalcorp/pp-react-text';
import { devWarning, utilStyles, uniqueId } from '@paypalcorp/pp-react-utils';
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21, _templateObject22, _templateObject23, _templateObject24, _templateObject25, _templateObject26;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysSpaceMd = tokens.sysSpaceMd,
    sysSpaceSm = tokens.sysSpaceSm,
    sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
    sysSpaceXs = tokens.sysSpaceXs,
    sysMotionDurationAction = tokens.sysMotionDurationAction,
    sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
    sysBorderThicknessFocus = tokens.sysBorderThicknessFocus,
    sysSizeMediaSm = tokens.sysSizeMediaSm,
    sysSizeMediaMd = tokens.sysSizeMediaMd,
    sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
    sysColorPrimaryMain = tokens.sysColorPrimaryMain,
    sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
    sysColorSecondaryMain = tokens.sysColorSecondaryMain,
    sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
    sysColorErrorMain = tokens.sysColorErrorMain;
  // 60px
  var selectionGroupBorderRadius = '0.375rem';
  var selectionGroupLabelPadding = math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)); // 11px

  var selectionGroupErrorLabelPadding = math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessEmphasis)); // 10px

  var selectionGroupErrorSelectedLabelPadding = math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessFocus)); // 9px

  var selectionGroupFocusBorderWidthInner = math("".concat(sysBorderThicknessNormal, " + ").concat(sysBorderThicknessEmphasis)); // 3px

  var selectionGroupFocusBorderHeightWidth = '0.125rem';
  var _textStyles = textStyles(tokens),
    caption = _textStyles.caption,
    body = _textStyles.body;
  var _utilStyles = utilStyles(tokens),
    elevationStyles = _utilStyles.elevationStyles,
    focusStyles = _utilStyles.focusStyles;
  var borderAfterStyles = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    content: '';\n    position: absolute;\n    top: -", ";\n    left: -", ";\n    right: 0;\n    border-radius: ", ";\n    width: ", ";\n    height: ", ";\n    pointer-events: none;\n  "])), sysBorderThicknessNormal, sysBorderThicknessNormal, selectionGroupBorderRadius, "calc(100% + ".concat(selectionGroupFocusBorderHeightWidth, ")"), "calc(100% + ".concat(selectionGroupFocusBorderHeightWidth, ")"));
  function secondaryStyle() {
    return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n      background-color: ", ";\n      color: ", ";\n      border-color: ", ";\n      border-width: ", ";\n      padding: ", ";\n      z-index: 1;\n      -webkit-text-stroke-width: ", "; // Hack to stop selection group from jumping since having a bold font-family will increase the width of the text and ultimately the width of the button\n    "])), sysColorBackgroundSurface, sysColorSecondaryMain, sysColorSecondaryMain, sysBorderThicknessNormal, selectionGroupLabelPadding, '0.0421875rem');
  }
  function verticalOrientation() {
    return css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      flex-direction: column;\n      min-width: auto;\n    "])));
  }
  var base = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: selection_group_base;\n    box-sizing: border-box;\n    border-style: none;\n    padding: 0;\n    text-align: left;\n    [dir='rtl'] & {\n      text-align: right;\n    }\n  "])));
  var buttons = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: buttons;\n    display: flex;\n    flex-wrap: wrap;\n    position: relative;\n    min-width: ", ";\n    margin-top: -", ";\n    margin-right: -", ";\n    [dir='rtl'] & {\n      margin-right: 0;\n      margin-left: -", ";\n    }\n  "])), '18rem', sysSpaceMd, sysSpaceMd, sysSpaceMd);
  var buttonsResponsiveXs = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: buttons_responsive_xs;\n    @media (max-width: ", ") {\n      ", ";\n    }\n  "])), '25rem', verticalOrientation());
  var buttonsResponsiveSm = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: buttons_responsive_sm;\n    @media (max-width: ", ") {\n      ", ";\n    }\n  "])), sysSizeMediaSm, verticalOrientation());
  var buttonsResponsiveMd = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: buttons_responsive_md;\n    @media (max-width: ", ") {\n      ", ";\n    }\n  "])), sysSizeMediaMd, verticalOrientation());
  var verticalButtons = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: buttons_vertical;\n    margin-top: 0;\n    display: grid;\n    grid-auto-rows: 1fr;\n    gap: ", ";\n    ", ";\n  "])), sysSpaceMd, verticalOrientation());
  var groupLabel = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: group_label;\n    ", ";\n    display: block;\n    color: ", ";\n    margin-bottom: ", ";\n  "])), body(), sysColorBackgroundHighContrast, sysSpaceMd);
  var helperText = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: helper_text;\n    ", ";\n    display: block;\n    list-style: none; // In case it's a list of helper texts\n    padding-left: 0;\n    margin: ", " 0 0 0;\n  "])), caption({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceSm);
  var errorText = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: error_text;\n    ", ";\n    display: flex;\n    align-items: center;\n    margin-top: ", ";\n    margin-bottom: 0;\n  "])), caption({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceXs);
  var errorIcon = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: error_icon;\n    color: ", ";\n    margin-right: ", ";\n    margin-left: initial;\n    [dir='rtl'] & {\n      margin-left: ", ";\n      margin-right: initial;\n    }\n  "])), sysColorErrorMain, sysSpaceXs, sysSpaceXs);
  var centerButtons = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: center;\n    justify-content: center;\n  "])));
  var justifyButtons = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: justify;\n    justify-content: stretch;\n  "])));
  var control = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: control;\n    position: absolute;\n    clip: rect(0, 0, 0, 0);\n    pointer-events: none;\n    /* We're hiding these so no need to use rems */\n    width: 1px;\n    height: 1px;\n    &:focus + label {\n      outline: none;\n      border-width: ", ";\n      padding: ", ";\n      &:hover {\n        border: ", " solid ", ";\n      }\n      &:after {\n        ", ";\n      }\n      ", ";\n    }\n    &:checked + label {\n      background-color: ", ";\n      color: ", ";\n      -webkit-text-stroke-width: inherit;\n      z-index: 1;\n      span {\n        color: ", ";\n      }\n      div {\n        color: ", ";\n      }\n      &:hover {\n        border-color: ", ";\n      }\n    }\n  "])), sysBorderThicknessNormal, selectionGroupLabelPadding, sysBorderThicknessNormal, sysColorSecondaryMain, borderAfterStyles, focusStyles.focusOffsetElement, sysColorPrimaryMain, sysColorBackgroundSurface, sysColorBackgroundSurface, sysColorBackgroundSurface, sysColorBackgroundSurface);
  var primaryLabelWhenFocusedAndChecked = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: primary_label_when_focused_and_checked;\n    input:checked:focus + label:after {\n      border: ", " solid\n        ", ";\n    }\n  "])), selectionGroupFocusBorderWidthInner, sysColorPrimaryMain);
  var label = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: label;\n    ", ";\n    ", ";\n    display: flex;\n    flex-direction: column;\n    justify-content: center;\n    background-color: ", ";\n    padding: ", ";\n    margin-top: ", ";\n    margin-right: ", ";\n    text-align: center;\n    cursor: pointer;\n    user-select: none;\n    border: ", " solid ", ";\n    border-radius: ", ";\n    position: relative;\n    transition: color ", " ease,\n      background-color ", " ease,\n      border-color ", " ease;\n    min-height: ", ";\n    min-width: ", ";\n    &:hover {\n      background: ", ";\n      border-color: ", ";\n    }\n    [dir='rtl'] & {\n      margin-right: 0;\n      margin-left: ", ";\n    }\n  "])), body(), elevationStyles.level1, sysColorBackgroundSurface, selectionGroupLabelPadding, sysSpaceMd, sysSpaceMd, sysBorderThicknessNormal, sysColorBackgroundSurface, selectionGroupBorderRadius, sysMotionDurationAction, sysMotionDurationAction, sysMotionDurationAction, '3.75rem', '5.125rem', sysColorBackgroundSurface, sysColorSecondaryMain, sysSpaceMd);
  var secondaryLabelVertical = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: secondary_label_vertical;\n    text-align: left;\n    [dir='rtl'] & {\n      text-align: right;\n    }\n  "])));
  var verticalLabel = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: label_vertical;\n    margin-right: 0;\n    margin-top: 0;\n  "])));
  var justifyLabel = css(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n    label: label_justify;\n    flex: 1 0 auto;\n  "])));
  var secondary = css(_templateObject22 || (_templateObject22 = _taggedTemplateLiteral(["\n    label: secondary;\n    input:checked + label {\n      ", ";\n      &:after {\n        ", ";\n        border: ", " solid ", ";\n      }\n      &:hover {\n        border: ", " solid ", ";\n      }\n      span {\n        color: ", ";\n      }\n    }\n    input:focus + label:after {\n      border-color: ", ";\n    }\n  "])), secondaryStyle(), borderAfterStyles, sysBorderThicknessFocus, sysColorSecondaryMain, sysBorderThicknessNormal, sysColorSecondaryMain, sysColorSecondaryMain, sysColorSecondaryMain);
  var secondaryLabelTwo = css(_templateObject23 || (_templateObject23 = _taggedTemplateLiteral(["\n    label: secondary_label_two;\n    ", ";\n  "])), caption({
    color: sysColorBackgroundMediumContrast
  }));
  var errorLabel = css(_templateObject24 || (_templateObject24 = _taggedTemplateLiteral(["\n    label: error_label;\n    border-width: ", ";\n    background-color: ", ";\n    border-color: ", ";\n    padding: ", ";\n  "])), sysBorderThicknessEmphasis, sysColorBackgroundSurface, sysColorErrorMain, selectionGroupErrorLabelPadding);
  var errorLabelWhenChecked = css(_templateObject25 || (_templateObject25 = _taggedTemplateLiteral(["\n    label: error_label_when_checked;\n    input:checked + label {\n      label: label_error_when_checked;\n      padding: ", ";\n      border: ", " solid ", ";\n      z-index: 1;\n      &:hover {\n        border: ", " solid ", ";\n      }\n      &:after {\n        border: none;\n      }\n    }\n  "])), selectionGroupErrorSelectedLabelPadding, sysBorderThicknessFocus, sysColorErrorMain, sysBorderThicknessFocus, sysColorErrorMain);
  var errorLabelWhenFocus = css(_templateObject26 || (_templateObject26 = _taggedTemplateLiteral(["\n    label: error_label_when_focus;\n    input:focus + label {\n      padding: ", ";\n      border: ", " solid ", ";\n      &:hover {\n        border-color: ", ";\n        border-width: ", ";\n      }\n      &:after {\n        border: none;\n      }\n    }\n  "])), selectionGroupErrorLabelPadding, sysBorderThicknessEmphasis, sysColorErrorMain, sysColorErrorMain, sysBorderThicknessEmphasis);
  return {
    base: base,
    buttons: buttons,
    buttonsResponsiveXs: buttonsResponsiveXs,
    buttonsResponsiveSm: buttonsResponsiveSm,
    buttonsResponsiveMd: buttonsResponsiveMd,
    verticalButtons: verticalButtons,
    centerButtons: centerButtons,
    justifyButtons: justifyButtons,
    groupLabel: groupLabel,
    helperText: helperText,
    errorText: errorText,
    errorIcon: errorIcon,
    control: control,
    label: label,
    verticalLabel: verticalLabel,
    justifyLabel: justifyLabel,
    primaryLabelWhenFocusedAndChecked: primaryLabelWhenFocusedAndChecked,
    secondary: secondary,
    secondaryLabelTwo: secondaryLabelTwo,
    errorLabel: errorLabel,
    errorLabelWhenChecked: errorLabelWhenChecked,
    errorLabelWhenFocus: errorLabelWhenFocus,
    secondaryLabelVertical: secondaryLabelVertical
  };
}
var FieldGroupMessage = function (_ref) {
  var errorText = _ref.errorText,
    errorIconAriaLabel = _ref.errorIconAriaLabel,
    helperText = _ref.helperText,
    msgId = _ref.msgId;
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]); // Get either the error message or helper text or... nothing

  return errorText && jsx("p", {
    css: _styles.errorText,
    id: msgId,
    "data-ppui": "true"
  }, jsx(CriticalIcon, {
    css: _styles.errorIcon,
    size: "sm",
    role: "img",
    "aria-label": errorIconAriaLabel,
    "data-ppui": "true"
  }), errorText) || helperText && jsx("p", {
    css: _styles.helperText,
    id: msgId,
    "data-ppui": "true"
  }, helperText) || null;
};
var _excluded = ["value", "label", "secondaryLabel", "id", "className", "onMouseOver"],
  _excluded2 = ["className", "errorText", "errorIconAriaLabel", "helperText", "id", "label", "hideLabel", "name", "value", "align", "responsive", "secondary", "options", "onChange", "alignContent"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
var renderSingleButtons = function (options, name, inputValue, onChange, error, msgId, align, _styles, alignContent, rest) {
  if (!options.length) {
    return jsx("em", {
      "data-ppui": "true"
    }, "No options to choose from");
  }
  return options.map(function (_ref, index) {
    var value = _ref.value,
      label = _ref.label,
      secondaryLabel = _ref.secondaryLabel,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? "selection_".concat(name, "_").concat(value) : _ref$id,
      className = _ref.className,
      onMouseOver = _ref.onMouseOver,
      optionRest = _objectWithoutProperties(_ref, _excluded);
    var checked = value === inputValue;
    var valueProps = onChange ? {
      onChange: onChange,
      value: value,
      checked: checked
    } : {
      value: value,
      defaultChecked: checked
    };
    var otherInputAttr = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, msgId && index === 0 && {
      'aria-describedby': msgId
    }), error && {
      'aria-invalid': true
    }), valueProps), rest), optionRest);
    var labelCSS = [_styles.label, align === 'vertical' && alignContent === 'left' && _styles.secondaryLabelVertical, error && _styles.errorLabel, align && _styles[align + 'Label']];
    return jsx(React.Fragment, {
      key: index
    }, jsx("input", _extends({
      type: "radio",
      name: name,
      id: id,
      css: _styles.control
    }, otherInputAttr, {
      "data-ppui": "true"
    })), jsx("label", _extends({
      className: className,
      css: labelCSS
    }, {
      htmlFor: id,
      onMouseOver: onMouseOver
    }, {
      "data-ppui": "true"
    }), label, secondaryLabel && jsx(CaptionText, _extends({
      css: _styles.secondaryLabelTwo
    }, typeof secondaryLabel === 'string' ? {
      as: 'span'
    } : null, align === 'vertical' ? {
      clampAtLine: 3
    } : null, {
      "data-ppui": "true"
    }), secondaryLabel)));
  });
};
var SingleSelectionGroup = function (_ref2) {
  var className = _ref2.className,
    errorText = _ref2.errorText,
    _ref2$errorIconAriaLa = _ref2.errorIconAriaLabel,
    errorIconAriaLabel = _ref2$errorIconAriaLa === void 0 ? 'Error' : _ref2$errorIconAriaLa,
    helperText = _ref2.helperText,
    _ref2$id = _ref2.id,
    id = _ref2$id === void 0 ? uniqueId('SelectionGroup_') : _ref2$id,
    label = _ref2.label,
    hideLabel = _ref2.hideLabel,
    _ref2$name = _ref2.name,
    name = _ref2$name === void 0 ? 'singleSelect' : _ref2$name,
    _ref2$value = _ref2.value,
    value = _ref2$value === void 0 ? '' : _ref2$value,
    align = _ref2.align,
    responsive = _ref2.responsive,
    secondary = _ref2.secondary,
    _ref2$options = _ref2.options,
    options = _ref2$options === void 0 ? [] : _ref2$options,
    onChange = _ref2.onChange,
    _ref2$alignContent = _ref2.alignContent,
    alignContent = _ref2$alignContent === void 0 ? 'center' : _ref2$alignContent,
    rest = _objectWithoutProperties(_ref2, _excluded2);
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var error = !!errorText;
  var msgId = errorText && "errorMessage_".concat(id) || helperText && "helperMessage_".concat(id);
  var containerCSS = [_styles.base, secondary && _styles.secondary];
  var buttonBarCSS = [_styles.buttons,
  //@ts-ignore
  align && _styles[align + 'Buttons'], !error && !secondary && _styles.primaryLabelWhenFocusedAndChecked, error && _styles.errorLabelWhenFocus, error && _styles.errorLabelWhenChecked, responsive &&
  // @ts-ignore
  _styles['buttonsResponsive' + responsive.charAt(0).toUpperCase() + responsive.charAt(1)]];
  return (
    //@ts-ignore
    jsx("fieldset", _extends({
      className: className,
      css: containerCSS,
      "aria-label": hideLabel ? label : null,
      "data-ppui-info": "selection-group_4.6.5"
    }, rest), !hideLabel && jsx("legend", {
      css: _styles.groupLabel,
      "data-ppui": "true"
    }, label), jsx("div", {
      css: buttonBarCSS,
      "data-ppui": "true"
    }, renderSingleButtons(options, name, value, onChange, error, msgId, align, _styles, alignContent, rest)), jsx(FieldGroupMessage, {
      errorText: errorText,
      errorIconAriaLabel: errorIconAriaLabel,
      helperText: helperText,
      msgId: msgId,
      "data-ppui": "true"
    }))
  );
};
var _excluded$1 = ["value", "label", "secondaryLabel", "name", "className", "checked", "onChange", "id", "onMouseOver"],
  _excluded2$1 = ["className", "errorText", "errorIconAriaLabel", "helperText", "id", "label", "hideLabel", "align", "responsive", "secondary", "options", "onChange", "alignContent"];
function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
var renderMultiButtons = function (options, masterOnChange, error, msgId, align, _styles, alignContent, rest) {
  if (!options.length) {
    return jsx("em", {
      "data-ppui": "true"
    }, "No options to choose from");
  }
  return options.map(function (_ref, index) {
    var value = _ref.value,
      label = _ref.label,
      secondaryLabel = _ref.secondaryLabel,
      name = _ref.name,
      className = _ref.className,
      checked = _ref.checked,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? masterOnChange : _ref$onChange,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? "selection_".concat(name) : _ref$id,
      onMouseOver = _ref.onMouseOver,
      optionRest = _objectWithoutProperties(_ref, _excluded$1);
    var valueProps = onChange ? {
      onChange: onChange,
      value: value,
      checked: checked
    } : {
      value: value,
      defaultChecked: checked
    };
    var otherInputAttr = _objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1({}, msgId && index === 0 && {
      'aria-describedby': msgId
    }), error && {
      'aria-invalid': true
    }), valueProps), rest), optionRest);
    var labelCSS = [_styles.label, align === 'vertical' && alignContent === 'left' && _styles.secondaryLabelVertical, error && _styles.errorLabel, align && _styles[align + 'Label']];
    return jsx(React.Fragment, {
      key: index
    }, jsx("input", _extends({
      type: "checkbox",
      name: name,
      id: id,
      css: _styles.control
    }, otherInputAttr, {
      "data-ppui": "true"
    })), jsx("label", _extends({
      className: className,
      css: labelCSS
    }, {
      htmlFor: id,
      onMouseOver: onMouseOver
    }, {
      "data-ppui": "true"
    }), label, secondaryLabel && jsx(CaptionText, _extends({
      css: _styles.secondaryLabelTwo
    }, typeof secondaryLabel === 'string' ? {
      as: 'span'
    } : null, align === 'vertical' ? {
      clampAtLine: 3
    } : null, {
      "data-ppui": "true"
    }), secondaryLabel)));
  });
};
var MultiSelectionGroup = function (_ref2) {
  var className = _ref2.className,
    errorText = _ref2.errorText,
    _ref2$errorIconAriaLa = _ref2.errorIconAriaLabel,
    errorIconAriaLabel = _ref2$errorIconAriaLa === void 0 ? 'Error' : _ref2$errorIconAriaLa,
    helperText = _ref2.helperText,
    _ref2$id = _ref2.id,
    id = _ref2$id === void 0 ? uniqueId('SelectionGroup_') : _ref2$id,
    _ref2$label = _ref2.label,
    label = _ref2$label === void 0 ? 'label' : _ref2$label,
    hideLabel = _ref2.hideLabel,
    align = _ref2.align,
    responsive = _ref2.responsive,
    secondary = _ref2.secondary,
    _ref2$options = _ref2.options,
    options = _ref2$options === void 0 ? [] : _ref2$options,
    onChange = _ref2.onChange,
    _ref2$alignContent = _ref2.alignContent,
    alignContent = _ref2$alignContent === void 0 ? 'center' : _ref2$alignContent,
    rest = _objectWithoutProperties(_ref2, _excluded2$1);
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var error = !!errorText;
  var msgId = errorText && "errorMessage_".concat(id) || helperText && "helperMessage_".concat(id);
  var containerCSS = [_styles.base, secondary && _styles.secondary];
  var buttonBarCSS = [_styles.buttons,
  //@ts-ignore
  align && _styles[align + 'Buttons'], !error && !secondary && _styles.primaryLabelWhenFocusedAndChecked, error && _styles.errorLabelWhenFocus, error && _styles.errorLabelWhenChecked, responsive &&
  // @ts-ignore
  _styles['buttonsResponsive' + responsive.charAt(0).toUpperCase() + responsive.charAt(1)]];
  return (
    //@ts-ignore
    jsx("fieldset", _extends({
      className: className,
      css: containerCSS,
      "aria-label": hideLabel ? label : null,
      "data-ppui-info": "selection-group_4.6.5"
    }, rest), !hideLabel && jsx("legend", {
      css: _styles.groupLabel,
      "data-ppui": "true"
    }, label), jsx("div", {
      css: buttonBarCSS,
      "data-ppui": "true"
    }, renderMultiButtons(options, onChange, error, msgId, align, _styles, alignContent, rest)), jsx(FieldGroupMessage, {
      errorText: errorText,
      errorIconAriaLabel: errorIconAriaLabel,
      helperText: helperText,
      msgId: msgId,
      "data-ppui": "true"
    }))
  );
};
export { MultiSelectionGroup, SingleSelectionGroup, styles as selectionGroupStyles };